import { Accessibility } from 'accessibility';

/**
 * תיקון מרוץ בהזרקת שורות הדיבור לתפריט הנגישות.
 *
 * הספרייה מוסיפה את "הקראת טקסט" ואת "דיבור להקלדה" בתוך injectTts, שהיא
 * async: היא ממתינה לרשימת הקולות של speechSynthesis. במקביל build() קובע
 * טיימר של 10ms שמריץ את addListeners (חיווט כל שורות התפריט) ואת
 * disableUnsupportedModulesAndSort (הסתרת מודולים כבויים ומיון). מי מהשניים
 * מקדים משתנה מריצה לריצה, ובריצה שבה הטיימר מקדים שתי השורות נכנסות לתפריט
 * אחריו: "דיבור להקלדה" מוצג למרות modules.speechToText: false, ושתי השורות
 * נשארות בלי מאזין - כפתור שנראה תקין ואינו עושה דבר.
 *
 * כאן מוחלפת injectTts בגרסה שמוסיפה את השורות סינכרונית, עוד לפני שהבנאי
 * חוזר ולכן בוודאות לפני הטיימר. ההמתנה לקולות נשארת, אבל היא כבר לא קובעת
 * אם השורה קיימת אלא רק אם היא מוצגת: בהיעדר קול בשפה המבוקשת השורה מקבלת
 * את המחלקה not-supported של הספרייה (display: none), בדיוק כמו מודול כבוי.
 *
 * הדריסה נשענת על מתודות ציבוריות בלבד - injectTts, options ו-parseKeys
 * מוצהרות ב-typings של הספרייה. אין נקודת התערבות מוקדמת יותר: build() רץ
 * בתוך הבנאי, ולכן אי אפשר לדרוס על המופע.
 */

const MENU_LIST_SELECTOR = '._access-menu ul';

/** המחלקה שהספרייה מסמנת בה מודול שאינו זמין; ה-CSS שלה מסתיר את השורה. */
const NOT_SUPPORTED_CLASS = 'not-supported';

/** שלוש דרגות מהירות ההקראה. addListeners מוצא אותן לפי שמות המחלקות האלה. */
const SPEECH_RATE_STEP_CLASSES = ['screen-reader-wrapper-step-1', 'screen-reader-wrapper-step-2', 'screen-reader-wrapper-step-3'];

/**
 * אחרי הזמן הזה מתייחסים לדפדפן כאילו אין לו קולות כלל. הספרייה עצמה מחכה
 * לנצח (setInterval שלא נעצר), וכאן זו דליפה מיותרת: לא כל דפדפן שולח
 * voiceschanged, ובלי מועד אחרון המאזין נשאר תלוי עד סוף חיי העמוד.
 */
const VOICES_TIMEOUT_MS = 3000;

const buildMenuItem = (action: string, label: string, title: string): HTMLLIElement => {
  const item = document.createElement('li');
  const button = document.createElement('button');

  button.setAttribute('data-access-action', action);
  button.setAttribute('title', title);
  button.appendChild(document.createTextNode(label));
  item.appendChild(button);

  return item;
};

/** שורת ההקראה נושאת גם את בורר מהירות הקריאה, במבנה שהספרייה מחווטת. */
const appendSpeechRateSteps = (item: HTMLLIElement): void => {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('class', 'screen-reader-wrapper');

  SPEECH_RATE_STEP_CLASSES.forEach((stepClass) => {
    const step = document.createElement('div');
    step.setAttribute('class', stepClass);
    step.setAttribute('tabIndex', '-1');
    wrapper.appendChild(step);
  });

  item.appendChild(wrapper);
};

const listVoices = (): Promise<SpeechSynthesisVoice[]> => {
  const synth = window.speechSynthesis;
  if (!synth) return Promise.resolve([]);

  const loaded = synth.getVoices();
  if (loaded.length > 0) return Promise.resolve(loaded);

  return new Promise((resolve) => {
    let timeout = 0;

    const finish = () => {
      window.clearTimeout(timeout);
      synth.removeEventListener('voiceschanged', finish);
      resolve(synth.getVoices());
    };

    timeout = window.setTimeout(finish, VOICES_TIMEOUT_MS);
    synth.addEventListener('voiceschanged', finish);
  });
};

let isPatched = false;

/**
 * חייבת לרוץ לפני new Accessibility - build() קורא ל-injectTts מתוך הבנאי.
 * קריאה חוזרת אינה עושה דבר, כך שאפשר לקרוא לה בכל mount.
 */
export const injectSpeechMenuItemsSynchronously = (): void => {
  if (isPatched) return;
  isPatched = true;

  Accessibility.prototype.injectTts = function injectTts(this: Accessibility): Promise<void> {
    const list = document.querySelector(MENU_LIST_SELECTOR);
    if (!list) return Promise.resolve();

    const { modules, labels, hotkeys, language } = this.options;
    if (!modules || !labels || !hotkeys) return Promise.resolve();

    if (modules.speechToText) {
      list.appendChild(buildMenuItem('speechToText', labels.speechToText, this.parseKeys(hotkeys.keys.speechToText)));
    }

    if (!modules.textToSpeech) return Promise.resolve();

    const reader = buildMenuItem('textToSpeech', labels.textToSpeech, this.parseKeys(hotkeys.keys.textToSpeech));
    appendSpeechRateSteps(reader);
    list.appendChild(reader);

    return listVoices().then((voices) => {
      // השורה כבר קיימת ומחווטת, ולכן ההסתרה המאוחרת בטוחה - להבדיל מהוספה
      // מאוחרת, שהיא כל הבאג. אם התפריט נבנה מחדש בינתיים השורה כבר מנותקת
      // מה-DOM, והמחלקה נוחתת על אלמנט שאיש לא רואה.
      if (!voices.some((voice) => voice.lang === language?.textToSpeechLang)) {
        reader.classList.add(NOT_SUPPORTED_CLASS);
      }
    });
  };
};
